import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UploadedImage {
  file: File;
  previewUrl: string;
  uploadedUrl?: string;
  imageId?: string;
}

interface UseChatImageUploadReturn {
  selectedImage: UploadedImage | null;
  handleImageSelect: (file: File, previewUrl: string) => void;
  handleImageRemove: () => void;
  uploadImageToStorage: (websiteId: string) => Promise<{ url: string; id: string } | null>;
  isUploading: boolean;
}

/**
 * Custom hook for managing image uploads in chat
 * Handles image selection, preview, upload to Supabase storage, and cleanup
 */
export const useChatImageUpload = (): UseChatImageUploadReturn => {
  const [selectedImage, setSelectedImage] = useState<UploadedImage | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Handle image selection
  const handleImageSelect = useCallback((file: File, previewUrl: string) => {
    setSelectedImage({ file, previewUrl });
  }, []);

  // Handle image removal
  const handleImageRemove = useCallback(() => {
    if (selectedImage?.previewUrl) {
      URL.revokeObjectURL(selectedImage.previewUrl);
    }
    setSelectedImage(null);
  }, [selectedImage]);

  /**
   * Upload the selected image to Supabase storage and images table
   * Returns the public URL and image ID
   */
  const uploadImageToStorage = useCallback(async (websiteId: string): Promise<{ url: string; id: string } | null> => {
    if (!selectedImage?.file || !websiteId) {
      console.error('No image selected or website ID missing');
      return null;
    }

    setIsUploading(true);
    try {
      // 1. Upload to storage
      const fileExt = selectedImage.file.name.split('.').pop();
      const fileName = `chat-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${websiteId}/${fileName}`;

      console.log('[useChatImageUpload] Uploading image to storage:', filePath);
      const { error: uploadError } = await supabase.storage
        .from('content-images')
        .upload(filePath, selectedImage.file);

      if (uploadError) {
        console.error('[useChatImageUpload] Upload error:', uploadError);
        throw uploadError;
      }

      // 2. Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('content-images')
        .getPublicUrl(filePath);
      
      console.log('[useChatImageUpload] Image uploaded, public URL:', publicUrl);

      // 3. Insert into images table
      const { data: imageInsert, error: imageInsertError } = await supabase
        .from('images')
        .insert({
          website_id: websiteId,
          name: selectedImage.file.name,
          url: publicUrl,
          size: selectedImage.file.size,
          type: selectedImage.file.type,
        })
        .select('id')
        .single();

      if (imageInsertError) {
        console.error('[useChatImageUpload] Image insert error:', imageInsertError);
        throw imageInsertError;
      }

      console.log('[useChatImageUpload] Image record created with ID:', imageInsert.id);

      // Update state with uploaded URL and ID
      setSelectedImage(prev => prev ? {
        ...prev,
        uploadedUrl: publicUrl,
        imageId: imageInsert.id
      } : null);

      return {
        url: publicUrl,
        id: imageInsert.id
      };
    } catch (error) {
      console.error('[useChatImageUpload] Error uploading image:', error);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setIsUploading(false);
    }
  }, [selectedImage]);

  // Cleanup preview URL on unmount or when image changes
  useEffect(() => {
    return () => {
      if (selectedImage?.previewUrl) {
        URL.revokeObjectURL(selectedImage.previewUrl);
      }
    };
  }, [selectedImage?.previewUrl]);

  return {
    selectedImage,
    handleImageSelect,
    handleImageRemove,
    uploadImageToStorage,
    isUploading,
  };
};
